import { useState } from "react";
import { X, Mail, Smartphone, Check, Loader2, Award, Copy, Share2 } from "lucide-react";
import { subscribe } from "../lib/api";
import { setStoredEmail } from "../lib/useNewsezeUser";

const TOPICS = ["Politics", "Markets", "World", "Tech", "Health", "Energy", "Local"];

const BADGES = {
  "calm-reader": { label: "Calm Reader", color: "#15815f" },
  "signal-keeper": { label: "Signal Keeper", color: "#a87b1d" },
};

export default function DailyBriefModal({ onClose, initialReferral = "" }) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [channel, setChannel] = useState("daily-brief");
  const [topics, setTopics] = useState(["Politics", "Markets"]);
  const [persona, setPersona] = useState("consumer");
  const [referredBy, setReferredBy] = useState(initialReferral);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const toggle = (t) => setTopics((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true); setErr("");
    try {
      const r = await subscribe({
        email,
        phone: channel === "sms-alerts" ? phone : null,
        channel,
        topics,
        persona,
        referred_by: (referredBy || "").trim().toUpperCase() || null,
      });
      setStoredEmail(r.email, r.referral_code);
      setResult(r);
    } catch (er) {
      setErr(er?.response?.data?.detail || "Could not subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const refUrl = result
    ? `${window.location.origin}/?ref=${result.referral_code}`
    : "";
  const refMsg = "I switched to Newseze for balanced, evidence-ranked news without the rage cycle. Try it:";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(refUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div className="modal-back" onClick={onClose} data-testid="daily-brief-modal">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[12px] uppercase tracking-[0.16em] font-black text-[var(--blue)] flex items-center gap-2">
              <Mail size={14} /> My Daily Brief
            </div>
            <h3 className="text-[22px] font-black mt-1 leading-tight">
              {result ? "You're on the list." : "Get the calm briefing."}
            </h3>
            <p className="text-[13.5px] text-[var(--muted)] mt-1">
              {result
                ? "First brief lands tomorrow morning. Earn the Calm Reader badge by inviting 3 friends."
                : "Top trust-ranked stories, calm headlines, what changed — delivered each morning."}
            </p>
          </div>
          <button onClick={onClose} className="text-[var(--muted)] hover:text-[var(--ink)]" aria-label="Close" data-testid="daily-brief-close">
            <X size={20} />
          </button>
        </div>

        {result ? (
          <div data-testid="daily-brief-success" className="space-y-4">
            <div className="p-4 rounded-md bg-[#e8f7f1] border border-[var(--green)]/30 text-[var(--green)] text-[14px] font-semibold flex items-center gap-2">
              <Check size={18} /> Subscribed as <span className="text-[var(--ink)] font-bold">{result.email}</span>
            </div>

            {/* Status / Streak */}
            <div className="rounded-md border border-[var(--line)] p-4" data-testid="reader-status">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--blue)]">Your Reader Status</div>
                {result.badge && BADGES[result.badge] && (
                  <span
                    className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.1em] px-2 py-1 rounded-full"
                    style={{ background: `${BADGES[result.badge].color}1A`, color: BADGES[result.badge].color }}
                  >
                    <Award size={12} /> {BADGES[result.badge].label}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center">
                  <div className="text-[24px] font-black text-[var(--ink)]">{result.streak_days || 1}</div>
                  <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-[var(--muted)]">Streak (days)</div>
                </div>
                <div className="text-center">
                  <div className="text-[24px] font-black text-[var(--ink)]">{result.referral_count || 0}</div>
                  <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-[var(--muted)]">Referrals</div>
                </div>
                <div className="text-center">
                  <div className="text-[18px] font-black text-[var(--blue)] tracking-tight">{result.referral_code}</div>
                  <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-[var(--muted)]">Your code</div>
                </div>
              </div>
            </div>

            {/* Referral block */}
            <div className="rounded-md border border-[var(--sky-2)] bg-[var(--sky)] p-4" data-testid="referral-block">
              <div className="text-[12px] uppercase tracking-[0.14em] font-black text-[var(--blue)] flex items-center gap-2">
                <Share2 size={12} /> Invite friends · Unlock Calm Reader badge
              </div>
              <p className="text-[13px] text-[var(--ink)] mt-1.5 leading-snug">
                Bring 3 friends and earn the <strong>Calm Reader</strong> badge in your morning brief.
                10 friends or a 30-day streak unlocks <strong>Signal Keeper</strong>.
              </p>
              <div className="flex gap-2 mt-3">
                <input className="field text-[13px]" value={refUrl} readOnly data-testid="referral-url" />
                <button onClick={copyLink} className="btn" data-testid="referral-copy">
                  {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-3">
                <a
                  className="btn icon justify-center"
                  target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer"
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(refMsg)}&url=${encodeURIComponent(refUrl)}`}
                  data-testid="referral-share-x"
                >
                  X / Twitter
                </a>
                <a
                  className="btn icon justify-center"
                  target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer"
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(refUrl)}`}
                  data-testid="referral-share-fb"
                >
                  Facebook
                </a>
                <a
                  className="btn icon justify-center"
                  target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer"
                  href={`https://wa.me/?text=${encodeURIComponent(refMsg + " " + refUrl)}`}
                  data-testid="referral-share-wa"
                >
                  WhatsApp
                </a>
                <a
                  className="btn icon justify-center"
                  href={`mailto:?subject=${encodeURIComponent("Newseze — balanced news")}&body=${encodeURIComponent(refMsg + " " + refUrl)}`}
                  data-testid="referral-share-email"
                >
                  Email
                </a>
              </div>
            </div>

            <button onClick={onClose} className="btn primary w-full justify-center">Done</button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setChannel("daily-brief")}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-md border ${channel === "daily-brief" ? "border-[var(--blue)] bg-[var(--sky)] text-[var(--blue)]" : "border-[var(--line)] text-[var(--muted)]"}`}
                data-testid="brief-channel-email">
                <Mail size={14} /> <span className="font-bold text-[13px]">Email</span>
              </button>
              <button type="button" onClick={() => setChannel("sms-alerts")}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-md border ${channel === "sms-alerts" ? "border-[var(--blue)] bg-[var(--sky)] text-[var(--blue)]" : "border-[var(--line)] text-[var(--muted)]"}`}
                data-testid="brief-channel-sms">
                <Smartphone size={14} /> <span className="font-bold text-[13px]">Text Message</span>
              </button>
            </div>

            <input type="email" required className="field" placeholder="you@email.com"
              value={email} onChange={(e) => setEmail(e.target.value)} data-testid="brief-email-input" />

            {channel === "sms-alerts" && (
              <input type="tel" className="field" placeholder="+1 555 123 4567"
                value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="brief-phone-input" />
            )}

            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] font-bold text-[var(--muted)] mb-2">Who are you reading for?</div>
              <div className="grid grid-cols-2 gap-2" data-testid="brief-persona-picker">
                <button type="button" onClick={() => setPersona("consumer")}
                  className={`chip !py-2 ${persona === "consumer" ? "!bg-[var(--blue)] !text-white !border-[var(--blue)]" : ""}`}
                  data-testid="brief-persona-consumer">
                  <div className="text-[12px] font-black">General reader</div>
                  <div className="text-[10px] opacity-80">5 trust-ranked stories across the spectrum</div>
                </button>
                <button type="button" onClick={() => setPersona("business")}
                  className={`chip !py-2 ${persona === "business" ? "!bg-[var(--blue)] !text-white !border-[var(--blue)]" : ""}`}
                  data-testid="brief-persona-business">
                  <div className="text-[12px] font-black">Business reader</div>
                  <div className="text-[10px] opacity-80">Markets · business · tech · AI · real estate focus</div>
                </button>
              </div>
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] font-bold text-[var(--muted)] mb-2">Topics</div>
              <div className="flex flex-wrap gap-1.5">
                {TOPICS.map((t) => (
                  <button key={t} type="button" onClick={() => toggle(t)}
                    className={`chip ${topics.includes(t) ? "!bg-[var(--blue)] !text-white !border-[var(--blue)]" : ""}`}
                    data-testid={`brief-topic-${t.toLowerCase()}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {referredBy && (
              <div className="text-[12px] text-[var(--green)] font-semibold" data-testid="referred-by-note">
                ✓ Invited by <span className="font-bold">{referredBy}</span>
              </div>
            )}

            {err && <div className="text-[var(--red)] text-[13px]">{err}</div>}

            <button type="submit" disabled={loading} className="btn primary w-full justify-center" data-testid="brief-submit">
              {loading ? <><Loader2 size={14} className="animate-spin" /> Subscribing…</> : "Subscribe (free)"}
            </button>
            <p className="text-[11px] text-[var(--muted)] text-center">
              You can unsubscribe in one tap. We never sell your data.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
